import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import Flex from 'legacyfrontend/components/Flex';
import { formatDate } from 'legacyfrontend/utils/dateUtils';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import { formatPrice } from 'legacyfrontend/helpers/formatPrice';
import { useFlightContext } from 'legacyfrontend/contexts/FlightDetailsContext';
import { DeferredFlightInfoRow } from './DeferredFlightInfoRow';
import { ArrivalCompanies, DepartureCompanies } from './FlightCompanies';
import { useLocation } from 'react-router-dom';
import FareDialog from '../FareDialog';
import ErrorSnackbar from 'legacyfrontend/components/ErrorSnackbar';
import {
  mergeAvailTotalIntoFirstFare,
  unwrapMultiFamilyFares,
} from 'legacyfrontend/helpers/mergeAvailTotalIntoFirstFare';
const FlightCard = ({ isBookingPage = false, ...flightData }) => {
  const { t } = useTranslation(['layout']);
  const { background, text } = useListingColors();
  const { selectFlight, handleFlightDetails, setOneWayFlight } = useFlightContext();
  const [selectedOutboundJourney, setSelectedOutboundJourney] = useState(null);
  const [selectedReturnJourney, setSelectedReturnJourney] = useState(null);
  const [tarifasData, setTarifasData] = useState([]);
  const [tarifasIsLoading, setTarifasIsLoading] = useState(false);
  const [isOutboundAccordionOpen, setIsOutboundAccordionOpen] = useState(false);
  const [isReturnAccordionOpen, setIsReturnAccordionOpen] = useState(false);
  const [isTarifasModalOpen, setIsTarifasModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const isOnlyOneWayParam = params?.get('isOnlyOneWay');
  const isOnlyOneWay = isOnlyOneWayParam === 'true';

  const { currency, flight, total } = flightData;

  const handleOutboundAccordion = () => {
    setIsOutboundAccordionOpen(!isOutboundAccordionOpen);
  };
  const handleReturnAccordion = () => {
    setIsReturnAccordionOpen(!isReturnAccordionOpen);
  };

  const cardId = useMemo(() => {
    const firstFlight = flight[0];
    return `${firstFlight.token}-${firstFlight.solutionId}`;
  }, [flight]);

  const handleFlightSelection = useCallback(
    (flight, type) => {
      let flightJourney;

      if (Array.isArray(flight) && flight[0]) {
        flightJourney = flight[0].journey;
      } else if (typeof flight === 'object' && flight !== null) {
        flightJourney = flight.journey;
      } else {
        const error = 'Flight data is undefined or malformed';
        setErrorMessage(error);
        setShowError(true);
        return;
      }

      if (!flightJourney) {
        const error = 'Flight journey is undefined';
        setErrorMessage(error);
        setShowError(true);
        return;
      }

      const newFlightData = {
        token: Array.isArray(flight) ? flight[0].token : flight.token,
        solutionId: Array.isArray(flight) ? flight[0].solutionId : flight.solutionId,
        journey: flightJourney,
      };

      if (type === 'ida') {
        const newValue = selectedOutboundJourney === flightJourney ? null : newFlightData;
        selectFlight(cardId, newValue, 'ida');
        setSelectedOutboundJourney(newValue ? flightJourney : null);
      } else {
        const newValue = selectedReturnJourney === flightJourney ? null : newFlightData;
        selectFlight(cardId, newValue, 'vuelta');
        setSelectedReturnJourney(newValue ? flightJourney : null);
      }
    },
    [selectFlight, cardId, selectedOutboundJourney, selectedReturnJourney]
  );

  const detectReferences = (flights) => {
    const references = [...new Set(flights.map((f) => f.originDestinationReferences))];
    return references;
  };

  const groupFlightsBySlug = (flights) => {
    const grouped = flights.reduce((acc, flight, index) => {
      if (!flight.slug) {
        acc[`direct_${index}`] = [flight];
      } else {
        if (!acc[flight.slug]) acc[flight.slug] = [];
        acc[flight.slug].push(flight);
      }
      return acc;
    }, {});
    return Object.values(grouped);
  };

  const separateFlights = (flights) => {
    const references = detectReferences(flights);
    const outboundReference = references[0];
    const returnReference = references[1];

    const outboundFlights = groupFlightsBySlug(
      flights.filter((f) => f.originDestinationReferences === outboundReference)
    );
    const returnFlights = groupFlightsBySlug(
      flights.filter((f) => f.originDestinationReferences === returnReference)
    );

    return { outboundFlights, returnFlights };
  };

  const { outboundFlights, returnFlights } = separateFlights(flight);

  const handleFlightFamily = useCallback(() => {
    if (
      (selectedOutboundJourney && selectedReturnJourney) ||
      (selectedOutboundJourney && isOnlyOneWay)
    ) {
      setTarifasIsLoading(true);
      handleFlightDetails(cardId)
        .then((result) => {
          if (result.success) {
            const fares = unwrapMultiFamilyFares(result.data);
            setTarifasData(mergeAvailTotalIntoFirstFare(fares, total));
            setIsTarifasModalOpen(true);
          } else {
            setErrorMessage(result.message || 'Error retrieving flight details');
            setShowError(true);
          }
        })
        .catch((error) => {
          setErrorMessage(error.message || 'Error retrieving flight details');
          setShowError(true);
        })
        .finally(() => {
          setTarifasIsLoading(false);
        });
    }
  }, [selectedOutboundJourney, selectedReturnJourney, handleFlightDetails, cardId, isOnlyOneWay, total]);

  useEffect(() => {
    setOneWayFlight(isOnlyOneWay);
  }, [isOnlyOneWay, setOneWayFlight]);

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <>
      <Stack className='flight-card' sx={{ flexDirection: { xs: 'column', lg: 'row' } }}>
        <Flex style={{ flexDirection: 'column', flex: 1 }}>
          {outboundFlights.length > 0 && (
            <Flex style={{ flexDirection: 'column', flex: 1 }}>
              <Flex className='flight_kind_header'>
                <h3 className='flight_kind_header_title'>Vuelo ida</h3>
                <p className='flight_kind_header_date'>
                  {formatDate(outboundFlights[0][0].departureDate)}
                </p>
                <DepartureCompanies iconColor={background} vuelos={outboundFlights} />
              </Flex>

              <DeferredFlightInfoRow
                flight={outboundFlights[0]}
                isBookingPage={isBookingPage}
                isSelected={
                  !isBookingPage && selectedOutboundJourney === outboundFlights[0][0].journey
                }
                kind='ida'
                onSelect={
                  !isBookingPage
                    ? () => handleFlightSelection(outboundFlights[0], 'ida')
                    : undefined
                }
              />

              {outboundFlights.length > 1 && (
                <DeferredFlightInfoRow
                  className='flight_card_option_spacing'
                  flight={outboundFlights[1]}
                  isBookingPage={isBookingPage}
                  isSelected={
                    !isBookingPage && selectedOutboundJourney === outboundFlights[1][0].journey
                  }
                  kind='ida'
                  onSelect={
                    !isBookingPage
                      ? () => handleFlightSelection(outboundFlights[1], 'ida')
                      : undefined
                  }
                />
              )}

              {outboundFlights.length > 2 && !isBookingPage && (
                <Accordion expanded={isOutboundAccordionOpen} onChange={handleOutboundAccordion}>
                  <AccordionSummary
                    aria-controls='panel1bh-content'
                    expandIcon={<ExpandMoreIcon />}
                    id='panel1bh-header'
                  >
                    <Typography
                      sx={{
                        width: '100%',
                        alignSelf: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      Ver más vuelos de ida
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {outboundFlights.slice(2).map((flight, index) => (
                      <DeferredFlightInfoRow
                        className='flight_card_option_spacing'
                        flight={flight}
                        isSelected={selectedOutboundJourney === flight[0].journey}
                        key={`outbound-${index}`}
                        kind='ida'
                        onSelect={() => handleFlightSelection(flight[0], 'ida')}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Flex>
          )}

          {returnFlights.length > 0 && (
            <Flex style={{ flexDirection: 'column', flex: 1 }}>
              <Flex className='flight_kind_header'>
                <h3 className='flight_kind_header_title'>Vuelo Vuelta</h3>
                <p className='flight_kind_header_date'>
                  {formatDate(returnFlights[0][0].departureDate)}
                </p>
                <ArrivalCompanies iconColor={background} vuelos={returnFlights} />
              </Flex>

              <DeferredFlightInfoRow
                flight={returnFlights[0]}
                isBookingPage={isBookingPage}
                isSelected={!isBookingPage && selectedReturnJourney === returnFlights[0][0].journey}
                kind='vuelta'
                onSelect={
                  !isBookingPage
                    ? () => handleFlightSelection(returnFlights[0], 'vuelta')
                    : undefined
                }
              />

              {returnFlights.length > 1 && (
                <DeferredFlightInfoRow
                  className='flight_card_option_spacing'
                  flight={returnFlights[1]}
                  isBookingPage={isBookingPage}
                  isSelected={
                    !isBookingPage && selectedReturnJourney === returnFlights[1][0].journey
                  }
                  kind='vuelta'
                  onSelect={
                    !isBookingPage
                      ? () => handleFlightSelection(returnFlights[1], 'vuelta')
                      : undefined
                  }
                />
              )}

              {returnFlights.length > 2 && !isBookingPage && (
                <Accordion expanded={isReturnAccordionOpen} onChange={handleReturnAccordion}>
                  <AccordionSummary
                    aria-controls='panel1bh-content'
                    expandIcon={<ExpandMoreIcon />}
                    id='panel1bh-header'
                  >
                    <Typography
                      sx={{
                        width: '100%',
                        alignSelf: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      Ver más vuelos de vuelta
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {returnFlights.slice(2).map((flight, index) => (
                      <DeferredFlightInfoRow
                        className='flight_card_option_spacing'
                        flight={flight}
                        isSelected={selectedReturnJourney === flight[0].journey}
                        key={`outbound-${index}`}
                        kind='ida'
                        onSelect={() => handleFlightSelection(flight[0], 'vuelta')}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </Flex>
          )}
        </Flex>
        {!isBookingPage && (
          <Stack
            className='flight_card__resume'
            sx={{
              maxWidth: { xs: 'inherit', lg: '200px' },
              flexDirection: { xs: 'row', lg: 'column' },
              justifyContent: { xs: 'space-between', lg: 'center' },
            }}
          >
            <Flex className='responsive-align' vertical>
              <p className='flight_kind_resume_title'>Ida y Vuelta</p>
              <p className='flight_price_resume_title'>{formatPrice(total, currency, true)}</p>
            </Flex>
            <Button
              className='flight_card_seleccionar_button'
              disabled={
                tarifasIsLoading ||
                !selectedOutboundJourney ||
                (!selectedReturnJourney && !isOnlyOneWay) ||
                (!selectedOutboundJourney && isOnlyOneWay)
              }
              onClick={handleFlightFamily}
              sx={{
                backgroundColor: background,
                color: text,
                '&.MuiButton-containedPrimary, &.MuiButton-textPrimary': {
                  backgroundColor: background,
                  color: text,
                },
                '&:disabled': {
                  backgroundColor: '#E5E7EB',
                  color: '#9CA3AF',
                  cursor: 'not-allowed',
                  opacity: 0.7,
                  boxShadow: 'none',
                },
                '&:hover': {
                  backgroundColor: background,
                  opacity:
                    !tarifasIsLoading && selectedOutboundJourney && selectedReturnJourney
                      ? 0.9
                      : 0.7,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {tarifasIsLoading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: text,
                    opacity: 0.8,
                  }}
                />
              ) : (
                'Reservar'
              )}
            </Button>
          </Stack>
        )}
      </Stack>
      {!isBookingPage && (
        <FareDialog
          currency={currency}
          data={tarifasData}
          onClose={() => setIsTarifasModalOpen(false)}
          open={isTarifasModalOpen}
        />
      )}
      <ErrorSnackbar message={errorMessage} onClose={handleCloseError} open={showError} />
    </>
  );
};

export default FlightCard;
